import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';

const SORT_OPTIONS = [
  { id: 'recent', label: 'Recientes' },
  { id: 'popular', label: 'Populares' },
  { id: 'cheap', label: 'Baratos' },
  { id: 'premium', label: 'Premium' },
];

const TABS = [
  { id: 'store', label: 'Tienda' },
  { id: 'owned', label: 'Mis comprados' },
  { id: 'created', label: 'Mis creados' },
];

const CUALstickerStore = () => {
  const [tab, setTab] = useState('store');
  const [packs, setPacks] = useState([]);
  const [owned, setOwned] = useState([]);
  const [created, setCreated] = useState(null); // { packs, summary }
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState('recent');
  const [selectedPack, setSelectedPack] = useState(null); // { pack, stickers, owned, is_creator }
  const [feedback, setFeedback] = useState(null);

  // Create pack form
  const [showCreate, setShowCreate] = useState(false);
  const [myStickers, setMyStickers] = useState([]);
  const [packName, setPackName] = useState('');
  const [packDesc, setPackDesc] = useState('');
  const [packPrice, setPackPrice] = useState(1);
  const [packStickerIds, setPackStickerIds] = useState([]);
  const [creating, setCreating] = useState(false);

  const fetchStore = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ sort });
      if (search) params.set('q', search);
      const res = await axios.get(`${API}/stickers/packs?${params}`);
      setPacks(res.data.packs || []);
    } catch (e) { /* ignore */ } finally { setLoading(false); }
  }, [sort, search]);

  const fetchOwned = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stickers/packs/mine/owned`);
      setOwned(res.data.packs || []);
    } catch (e) { /* ignore */ }
  }, []);

  const fetchCreated = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stickers/packs/mine/created`);
      setCreated({ packs: res.data.packs || [], summary: res.data.summary || {} });
    } catch (e) { /* ignore */ }
  }, []);

  const fetchMyStickers = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/stickers/my-stickers`);
      setMyStickers(res.data.stickers || []);
    } catch (e) { /* ignore */ }
  }, []);

  useEffect(() => { fetchStore(); }, [fetchStore]);
  useEffect(() => {
    if (tab === 'owned') fetchOwned();
    if (tab === 'created') { fetchCreated(); fetchMyStickers(); }
  }, [tab, fetchOwned, fetchCreated, fetchMyStickers]);

  const openPack = async (packId) => {
    try {
      const res = await axios.get(`${API}/stickers/packs/${packId}`);
      setSelectedPack(res.data);
    } catch (e) {
      setFeedback({ type: 'error', text: 'No se pudo abrir el pack' });
    }
  };

  const buyPack = async (packId) => {
    setFeedback(null);
    try {
      const res = await axios.post(`${API}/stickers/packs/${packId}/buy`);
      if (res.data.already_owned) {
        setFeedback({ type: 'success', text: 'Ya posees este pack' });
      } else {
        setFeedback({
          type: 'success',
          text: `Pack adquirido. Pagaste ${res.data.price_paid} Cc (${res.data.creator_revenue} Cc al creador, ${res.data.platform_fee_burned} Cc quemados). Saldo: ${res.data.new_balance} Cc`,
        });
      }
      fetchOwned();
      fetchStore();
      // refresh selected pack detail
      if (selectedPack) openPack(packId);
    } catch (e) {
      setFeedback({ type: 'error', text: e?.response?.data?.detail || 'Error en la compra' });
    }
  };

  const togglePackSticker = (id) => {
    setPackStickerIds(prev => {
      if (prev.includes(id)) return prev.filter(x => x !== id);
      if (prev.length >= 24) return prev;
      return [...prev, id];
    });
  };

  const createPack = async () => {
    setCreating(true);
    setFeedback(null);
    try {
      const res = await axios.post(`${API}/stickers/packs`, {
        name: packName,
        description: packDesc,
        price_cualcoin: parseFloat(packPrice),
        sticker_ids: packStickerIds,
      });
      setFeedback({ type: 'success', text: `Pack '${res.data.pack.name}' creado en draft. Publicalo cuando estes listo.` });
      setShowCreate(false);
      setPackName(''); setPackDesc(''); setPackPrice(1); setPackStickerIds([]);
      fetchCreated();
    } catch (e) {
      setFeedback({ type: 'error', text: e?.response?.data?.detail || 'Error creando pack' });
    } finally { setCreating(false); }
  };

  const togglePublish = async (packId) => {
    try {
      const res = await axios.post(`${API}/stickers/packs/${packId}/publish`);
      setFeedback({ type: 'success', text: `Pack ${res.data.status === 'published' ? 'publicado' : 'despublicado'}` });
      fetchCreated();
    } catch (e) {
      setFeedback({ type: 'error', text: e?.response?.data?.detail || 'Error' });
    }
  };

  const deletePack = async (packId) => {
    if (!window.confirm('Eliminar este pack? Solo se permite si no tiene ventas.')) return;
    try {
      await axios.delete(`${API}/stickers/packs/${packId}`);
      setFeedback({ type: 'success', text: 'Pack eliminado' });
      fetchCreated();
    } catch (e) {
      setFeedback({ type: 'error', text: e?.response?.data?.detail || 'Error' });
    }
  };

  const renderPackCard = (p, opts = {}) => (
    <div key={p.id} data-testid={`pack-card-${p.id}`}
      className="rounded-xl border border-gray-100 bg-white p-4 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <h4 className="text-sm font-bold text-gray-900 truncate">{p.name}</h4>
            {p.status === 'draft' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-100 text-gray-600">Borrador</span>}
            {p.status === 'unpublished' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700">Despublicado</span>}
            {p.status === 'published' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-green-100 text-green-700">Publicado</span>}
          </div>
          {p.description && <p className="text-xs text-gray-500 line-clamp-2 mb-1">{p.description}</p>}
          <p className="text-[11px] text-gray-400">por {p.creator_name} · {p.sticker_ids?.length || 0} stickers</p>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-base font-bold text-purple-700">{p.price_cualcoin} Cc</div>
          {p.sales_count > 0 && <div className="text-[11px] text-gray-500">{p.sales_count} ventas</div>}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {opts.purchasedAt && (
          <span className="text-[11px] px-2 py-0.5 rounded-full bg-green-100 text-green-700">
            Comprado {new Date(opts.purchasedAt).toLocaleDateString('es-VE')}
          </span>
        )}
        <button onClick={() => openPack(p.id)} data-testid={`pack-detail-${p.id}`}
          className="ml-auto text-xs px-3 py-1 rounded-full bg-purple-600 text-white hover:bg-purple-700">
          Ver pack
        </button>
        {opts.creatorActions && (
          <>
            <button onClick={() => togglePublish(p.id)} data-testid={`pack-publish-${p.id}`}
              className="text-xs px-3 py-1 rounded-full border border-purple-300 text-purple-700 hover:bg-purple-50">
              {p.status === 'published' ? 'Despublicar' : 'Publicar'}
            </button>
            {p.sales_count === 0 && (
              <button onClick={() => deletePack(p.id)} data-testid={`pack-delete-${p.id}`}
                className="text-xs px-3 py-1 rounded-full border border-red-300 text-red-700 hover:bg-red-50">
                Eliminar
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="bg-white rounded-2xl shadow-sm p-6" data-testid="cualsticker-store">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">CUALsticker Store</h2>
          <p className="text-xs text-gray-500 mt-0.5">Compra y vende packs premium. 90% para el creador, 10% se quema.</p>
        </div>
      </div>

      <div className="flex gap-2 mb-5 border-b border-gray-100">
        {TABS.map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            data-testid={`store-tab-${t.id}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px ${
              tab === t.id ? 'border-purple-600 text-purple-700' : 'border-transparent text-gray-500 hover:text-gray-800'
            }`}>
            {t.label}
          </button>
        ))}
      </div>

      {feedback && (
        <div data-testid="store-feedback" className={`mb-4 text-sm rounded-lg px-3 py-2 ${
          feedback.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-700'
        }`}>{feedback.text}</div>
      )}

      {tab === 'store' && (
        <div className="space-y-4">
          <div className="flex flex-wrap gap-2 items-center">
            <input type="text" value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Buscar packs..."
              data-testid="store-search"
              className="flex-1 min-w-[200px] px-3 py-2 border border-gray-200 rounded-lg text-sm" />
            <select value={sort} onChange={e => setSort(e.target.value)}
              data-testid="store-sort"
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm">
              {SORT_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
            </select>
          </div>
          {loading ? (
            <div className="p-6 text-center text-sm text-gray-400">Cargando...</div>
          ) : packs.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400" data-testid="store-empty">No hay packs publicados aun.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {packs.map(p => renderPackCard(p))}
            </div>
          )}
        </div>
      )}

      {tab === 'owned' && (
        <div className="space-y-3" data-testid="owned-packs">
          {owned.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Aun no has comprado packs.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {owned.map(p => renderPackCard(p, { purchasedAt: p.purchased_at }))}
            </div>
          )}
        </div>
      )}

      {tab === 'created' && (
        <div className="space-y-4" data-testid="created-packs">
          {created?.summary && (
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-purple-50">
                <div className="text-xs text-purple-700">Ventas totales</div>
                <div className="text-xl font-bold text-purple-900" data-testid="created-summary-sales">{created.summary.total_sales || 0}</div>
              </div>
              <div className="p-3 rounded-lg bg-green-50">
                <div className="text-xs text-green-700">Ingresos (Cc)</div>
                <div className="text-xl font-bold text-green-900" data-testid="created-summary-revenue">{created.summary.total_revenue_cualcoin || 0}</div>
              </div>
              <div className="p-3 rounded-lg bg-orange-50">
                <div className="text-xs text-orange-700">Quemado (Cc)</div>
                <div className="text-xl font-bold text-orange-900" data-testid="created-summary-burned">{created.summary.total_burned_cualcoin || 0}</div>
              </div>
            </div>
          )}
          <button onClick={() => setShowCreate(true)} data-testid="open-create-pack"
            className="px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700">
            + Crear nuevo pack
          </button>
          {created?.packs?.length === 0 ? (
            <div className="p-6 text-center text-sm text-gray-400">Aun no has creado packs.</div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {(created?.packs || []).map(p => renderPackCard(p, { creatorActions: true }))}
            </div>
          )}
        </div>
      )}

      {/* Modal: Create Pack */}
      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}
            data-testid="create-pack-modal">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-lg font-bold">Crear pack premium</h3>
              <p className="text-xs text-gray-500">Agrupa hasta 24 de tus stickers y ponles precio.</p>
            </div>
            <div className="p-5 space-y-3">
              <input type="text" value={packName} onChange={e => setPackName(e.target.value)}
                placeholder="Nombre del pack" maxLength={60}
                data-testid="create-pack-name"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <textarea value={packDesc} onChange={e => setPackDesc(e.target.value)}
                placeholder="Descripcion (opcional)" maxLength={400} rows={2}
                data-testid="create-pack-desc"
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">Precio (Cc)</label>
                <input type="number" value={packPrice} onChange={e => setPackPrice(e.target.value)}
                  step="0.01" min="0.01" max="1000"
                  data-testid="create-pack-price"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm" />
                <p className="text-[11px] text-gray-500 mt-1">
                  Recibiras {(parseFloat(packPrice || 0) * 0.9).toFixed(4)} Cc por venta · {(parseFloat(packPrice || 0) * 0.1).toFixed(4)} Cc se queman
                </p>
              </div>
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1">
                  Stickers ({packStickerIds.length}/24)
                </label>
                {myStickers.length === 0 ? (
                  <p className="text-xs text-gray-500 p-3 bg-gray-50 rounded-lg">
                    Necesitas crear stickers primero (desde el chat: + Crear sticker).
                  </p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-64 overflow-y-auto">
                    {myStickers.map(s => {
                      const checked = packStickerIds.includes(s.id);
                      return (
                        <button key={s.id} onClick={() => togglePackSticker(s.id)}
                          data-testid={`pack-sticker-toggle-${s.id}`}
                          className={`relative p-2 rounded-lg border-2 ${
                            checked ? 'border-purple-600 bg-purple-50' : 'border-gray-200 bg-white'
                          }`}>
                          {s.url ? (
                            <img src={s.url} alt={s.label} className="w-full h-16 object-contain" />
                          ) : (
                            <div className="w-full h-16 bg-gray-100 rounded" />
                          )}
                          <div className="text-[10px] text-gray-700 truncate mt-1">{s.label}</div>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-2">
              <button onClick={() => setShowCreate(false)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm">Cancelar</button>
              <button onClick={createPack}
                disabled={creating || !packName || packStickerIds.length === 0}
                data-testid="create-pack-submit"
                className="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold disabled:bg-gray-300">
                {creating ? 'Creando...' : 'Crear pack'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Pack Detail */}
      {selectedPack && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setSelectedPack(null)}>
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}
            data-testid="pack-detail-modal">
            <div className="p-5 border-b border-gray-100 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h3 className="text-lg font-bold">{selectedPack.pack.name}</h3>
                <p className="text-xs text-gray-500">por {selectedPack.pack.creator_name} · {selectedPack.stickers.length} stickers</p>
                {selectedPack.pack.description && <p className="text-sm text-gray-700 mt-2">{selectedPack.pack.description}</p>}
              </div>
              <div className="text-right">
                <div className="text-2xl font-bold text-purple-700">{selectedPack.pack.price_cualcoin} Cc</div>
              </div>
            </div>
            <div className="p-5">
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {selectedPack.stickers.map(s => (
                  <div key={s.id} className="p-2 rounded-lg border border-gray-100">
                    {s.url ? (
                      <img src={s.url} alt={s.label} className="w-full h-20 object-contain" />
                    ) : (
                      <div className="w-full h-20 bg-gray-100 rounded" />
                    )}
                    <div className="text-[10px] text-gray-700 truncate mt-1">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-5 border-t border-gray-100 flex gap-2">
              <button onClick={() => setSelectedPack(null)}
                className="flex-1 px-4 py-2 rounded-lg border border-gray-300 text-sm">Cerrar</button>
              {selectedPack.is_creator ? (
                <span className="flex-1 px-4 py-2 rounded-lg bg-gray-100 text-center text-sm text-gray-500">Tu pack</span>
              ) : selectedPack.owned ? (
                <span className="flex-1 px-4 py-2 rounded-lg bg-green-100 text-green-800 text-center text-sm font-semibold">Ya lo posees</span>
              ) : (
                <button onClick={() => buyPack(selectedPack.pack.id)} data-testid={`pack-buy-${selectedPack.pack.id}`}
                  className="flex-1 px-4 py-2 rounded-lg bg-purple-600 text-white text-sm font-semibold hover:bg-purple-700">
                  Comprar por {selectedPack.pack.price_cualcoin} Cc
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CUALstickerStore;
